<template>
  <div class="phone-page">
    <title-bar v-if="!isFromGoogle" :title="$lang.phoneUnbound_title || 'PHONE SECURITY'" />
    <!-- Custom header for Google forced bind -->
    <nav v-else class="phone-header">
      <button class="phone-header__back" @click="showLogoutConfirm = true">
        <van-icon name="cross" size="20" color="#ffffff" />
      </button>
      <h1 class="phone-header__title">{{ $lang.phoneUnbound_title || 'PHONE SECURITY' }}</h1>
      <div class="phone-header__spacer"></div>
    </nav>

    <div class="phone-content">
      <!-- Icon -->
      <div class="phone-icon">
        <van-icon name="phone-o" size="48" color="#ff5a1f" />
      </div>

      <p class="phone-desc">{{ $lang.phoneUnbound_desc || 'Link your phone number to enhance account security, enable SMS alerts, and recover your account easily.' }}</p>

      <!-- Phone Input -->
      <div class="phone-field-row">
        <div class="phone-area-code" @click="toggleCountry">
          <img :src="selectedIcon || GET_ICONURL" width="22" />
          <span class="phone-area-code__text">{{ selectedAreaCode || GET_AREACODE }}</span>
          <van-icon :name="showCountryDrop ? 'arrow-up' : 'arrow-down'" size="10" color="var(--vk-t2)" />
        </div>
        <van-field
          v-model="phoneNo"
          :placeholder="$lang.gpb_phone_placeholder || 'Enter phone number'"
          class="phone-input"
          type="number"
          @input="phoneNo = phoneNo.replace(/\D/g, '').slice(0, 13)"
        />
      </div>

      <!-- Country Dropdown -->
      <div v-if="showCountryDrop" class="phone-dropdown">
        <div
          v-for="(item, idx) in countryList"
          :key="idx"
          class="phone-dropdown__item"
          @click="selectCountry(item)"
        >
          <img :src="item.iconUrl" width="24" />
          <span>{{ item.countryName }}</span>
          <span class="phone-dropdown__code">{{ item.areaCode }}</span>
        </div>
        <div v-if="countryList.length === 0" class="phone-dropdown__item" style="justify-content: center; color: var(--vk-t3);">
          {{ $lang.common_loading || 'Loading...' }}
        </div>
      </div>

      <p v-if="errorMsg" class="phone-error">
        <van-icon name="warning" color="var(--vk-danger)" size="12" /> {{ errorMsg }}
      </p>
      <p v-if="countryReadError" class="phone-error">
        <van-icon name="warning" color="var(--vk-danger)" size="12" /> {{ countryReadError }}
      </p>
      <p v-if="bindBlockedReason" class="phone-error">
        <van-icon name="warning" color="var(--vk-danger)" size="12" /> {{ bindBlockedReason }}
      </p>

      <!-- Submit Button -->
      <button
        class="phone-submit"
        :class="{ 'phone-submit--disabled': !phoneNo || loading || bindBlockedReason }"
        :disabled="!phoneNo || loading || bindBlockedReason"
        @click="handleBind"
      >
        {{ loading ? ($lang.common_loading || 'Loading...') : ($lang.phoneUnbound_bind || 'BIND PHONE') }}
      </button>
    </div>

    <!-- Logout confirm for Google forced bind -->
    <van-dialog
      v-model="showLogoutConfirm"
      :title="$lang.gpb_confirm_title || 'Notice'"
      :message="$lang.gpb_confirm_msg || 'Not binding phone will log you out. Are you sure?'"
      show-cancel-button
      :confirm-button-text="$lang.gpb_confirm_yes || 'Yes'"
      :cancel-button-text="$lang.gpb_confirm_no || 'No'"
      confirm-button-color="var(--vk-danger)"
      cancel-button-color="#ff5a1f"
      @confirm="confirmLogout"
      @cancel="showLogoutConfirm = false"
    />
  </div>
</template>

<script>
import { BindPhone, GetPlatformList, Logout } from '@/api/common'
import { failMessage, handleNeedLogin, hasProvenEmptyPlatformList, isConfirmedLogout, isOk, isSafeWriteSuccess, normalizePlatformList } from './safeFlow'
import { safeGetStorageItem, safeLocalStorage, safeRemoveStorageItem, safeSetStorageItem } from '@/utils/safeStorage'

function phoneFailureMessage(error, fallback) {
  const message = failMessage(error, fallback)
  return /^network error$/i.test(String(message || '').trim()) ? fallback : message
}

const MINE_BLOCKED_MARKER_KEYS = ['failClosed', 'fail_closed', 'falseProof', 'false_proof', 'readOnly', 'read_only', 'readOnlyAction', 'read_only_action', 'readOnlyMode', 'read_only_mode', 'readBlocked', 'read_blocked', 'writeBlocked', 'write_blocked', 'backendBlocked', 'backend_blocked', 'backendFailure', 'backend_failure', 'technicalMarker', 'technical_marker', 'mock', 'fallback']
const MINE_FALSE_PROOF_KEYS = ['writeProof', 'write_proof', 'affectedRowsProof', 'affected_rows_proof']
const ACCOUNT_BIND_STRICT_WRITE_PROOF_KEYS = ['writeProof', 'write_proof', 'serverProof', 'server_proof', 'affectedProof', 'affected_proof', 'affectedRowsProof', 'affected_rows_proof']
const ACCOUNT_BIND_STRICT_WRITE_COUNT_KEYS = ['affected', 'affectedRows', 'affected_rows', 'rowCount', 'row_count', 'rowsAffected', 'rows_affected']
const ACCOUNT_BIND_FALSE_PROOF_TOKENS = ['0', 'false', 'true', 'null', 'undefined', 'none', 'n/a', 'na', 'mock', 'placeholder']
const ACCOUNT_BIND_BLOCKED_PROOF_TOKENS = ['stub', 'mock', 'fallback', 'placeholder', 'read_only', 'readonly', 'query_only', 'only_query', 'unavailable', 'unsupported', 'failclosed', 'fail_closed', 'writeblocked', 'write_blocked', 'backendblocked', 'backend_blocked', 'backendfailure', 'backend_failure', 'technicalmarker', 'technical_marker', 'falseproof', 'false_proof']

function accountBindToken(value) {
  return String(value === undefined || value === null ? '' : value).trim().toLowerCase().replace(/[.!。！]+$/g, '').replace(/[\s-]+/g, '_')
}

function isActiveMineBlockedMarker(value) {
  if (value === false || value === 0 || value === null || value === undefined || value === '') return false
  return !['false', '0', 'no', 'off'].includes(accountBindToken(value))
}

function hasMineBlockedWriteMarker(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasMineBlockedWriteMarker(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    const token = accountBindToken(value)
    if (MINE_BLOCKED_MARKER_KEYS.includes(key) && isActiveMineBlockedMarker(value)) return true
    if (MINE_FALSE_PROOF_KEYS.includes(key) && (value === false || value === 0 || ['false', '0', 'no', 'off', 'placeholder'].includes(token))) return true
    if (token === 'placeholder') return true
    return value && typeof value === 'object' && hasMineBlockedWriteMarker(value, depth + 1, seen)
  })
}

function hasAccountBindPositiveWriteCount(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value !== 'string') return false
  const token = accountBindToken(value)
  return /^\d+$/.test(token) && !/^0+$/.test(token)
}

function hasAccountBindStrictProofValue(value) {
  if (Array.isArray(value)) {
    return value.length > 0 && value.every(item => hasAccountBindStrictProofValue(item))
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).length > 0 && !hasMineBlockedWriteMarker(value)
  }
  if (typeof value === 'boolean') return false
  const token = accountBindToken(value)
  return !!token &&
    !ACCOUNT_BIND_FALSE_PROOF_TOKENS.includes(token) &&
    !ACCOUNT_BIND_BLOCKED_PROOF_TOKENS.some(blocked => token.includes(blocked)) &&
    !token.includes('fail') &&
    !token.includes('error')
}

function hasAccountBindStrictWriteProof(payload, depth = 0, seen = []) {
  if (!payload || typeof payload !== 'object' || depth > 6 || seen.includes(payload)) return false
  seen.push(payload)
  if (Array.isArray(payload)) return payload.some(item => hasAccountBindStrictWriteProof(item, depth + 1, seen))
  return Object.keys(payload).some(key => {
    const value = payload[key]
    if (ACCOUNT_BIND_STRICT_WRITE_COUNT_KEYS.includes(key)) return hasAccountBindPositiveWriteCount(value)
    if (ACCOUNT_BIND_STRICT_WRITE_PROOF_KEYS.includes(key)) return hasAccountBindStrictProofValue(value)
    return value && typeof value === 'object' && hasAccountBindStrictWriteProof(value, depth + 1, seen)
  })
}

function isMineSafeWriteSuccess(res) {
  return isSafeWriteSuccess(res) && hasAccountBindStrictWriteProof(res) && !hasMineBlockedWriteMarker(res)
}

function phoneLogoutFailureMessage(error, fallback) {
  const message = phoneFailureMessage(error, fallback)
  return /^(ok|success|successful|succeed|succeeded|done|complete|completed)$/i.test(String(message || '').trim()) ? fallback : message
}

function reloadLocationSafely() {
  try {
    const location = typeof window !== 'undefined' && window ? window.location : null
    if (location && typeof location.reload === 'function') {
      location.reload()
      return true
    }
  } catch (e) {
    return false
  }
  return false
}

function getLocalStorage() {
  try {
    return typeof safeLocalStorage === 'function' ? safeLocalStorage() : null
  } catch (e) {
    return null
  }
}

export default {
  name: 'PhoneUnbound',
  data() {
    return {
      phoneNo: '',
      loading: false,
      errorMsg: '',
      showCountryDrop: false,
      showLogoutConfirm: false,
      countryList: [],
      selectedIcon: safeGetStorageItem(getLocalStorage(), 'iconUrl'),
      selectedAreaCode: safeGetStorageItem(getLocalStorage(), 'areaCode'),
      countryReadError: '',
      bindBlockedReason: ''
    }
  },
  computed: {
    isFromGoogle() {
      return this.$route.query.fromGoogle === '1'
    }
  },
  mounted() {
    if (!this.isFromGoogle) this.ensureLogin()
  },
  methods: {
    homePath() {
      const prefix = this.$route.params.prefix || safeGetStorageItem(getLocalStorage(), 'country') || ''
      return prefix ? '/' + prefix + '/home' : '/home'
    },
    ensureLogin() {
      if (safeGetStorageItem(getLocalStorage(), 'token')) return true
      if (this.$bus) this.$bus.$emit('openLogin')
      this.$router.replace(this.homePath())
      return false
    },
    finishLogout(clearToken) {
      if (this.isFromGoogle) safeRemoveStorageItem(getLocalStorage(), 'googleThirdUuid')
      if (clearToken) safeRemoveStorageItem(getLocalStorage(), 'token')
      this.showLogoutConfirm = false
      this.$router.replace(this.homePath())
      setTimeout(() => { reloadLocationSafely() }, 200)
    },
    async confirmLogout() {
      const hasToken = !!safeGetStorageItem(getLocalStorage(), 'token')
      if (!hasToken) {
        this.finishLogout(false)
        return
      }
      try {
        const data = await Logout()
        if (isConfirmedLogout(data)) {
          this.finishLogout(true)
        } else {
          this.$toast({ message: phoneLogoutFailureMessage(data, 'This feature is temporarily unavailable. Please try again later.'), icon: 'cross' })
        }
      } catch (e) {
        this.$toast({ message: phoneLogoutFailureMessage(e, '当前退出登录请求失败，请稍后再试。'), icon: 'cross' })
      }
    },
    toggleCountry() {
      this.showCountryDrop = !this.showCountryDrop
      if (this.showCountryDrop && this.countryList.length === 0) {
        this.fetchCountryList()
      }
    },
    async fetchCountryList() {
      try {
        const data = await GetPlatformList()
        if (handleNeedLogin(this, data, this.$lang.login_txt11 || this.$lang.Entrar || 'Login Now', { isValue: 'isLogin' }, { replace: true })) return
        if (isOk(data)) {
          const previousCountryList = this.countryList
          const isProvenEmptyList = hasProvenEmptyPlatformList(data)
          this.countryList = normalizePlatformList(data)
          if (!this.countryList.length && !isProvenEmptyList) {
            this.countryList = previousCountryList
            this.countryReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
            this.$toast({ message: this.countryReadError, icon: 'cross' })
            return
          }
          this.countryReadError = ''
        } else {
          this.countryReadError = failMessage(data, 'This feature is temporarily unavailable. Please try again later.')
          this.$toast({ message: this.countryReadError, icon: 'cross' })
        }
      } catch (e) {
        this.countryReadError = phoneFailureMessage(e, '当前国家区号列表读取失败，已保留已加载数据。')
        this.$toast({ message: this.countryReadError, icon: 'cross' })
      }
    },
    selectCountry(item) {
      const platform = normalizePlatformList([item])[0]
      if (!platform.areaCode && !platform.country && !platform.platform) return
      this.selectedIcon = platform.iconUrl
      this.selectedAreaCode = platform.areaCode
      safeSetStorageItem(getLocalStorage(), 'areaCode', platform.areaCode)
      safeSetStorageItem(getLocalStorage(), 'iconUrl', platform.iconUrl)
      this.showCountryDrop = false
    },
    async handleBind() {
      if (this.bindBlockedReason) return
      if (!this.isFromGoogle && !this.ensureLogin()) return
      if (!this.phoneNo) {
        this.errorMsg = this.$lang.login_txt19 || 'Please enter the content'
        return
      }
      if (!/^\d{8,13}$/.test(this.phoneNo)) {
        this.errorMsg = this.$lang.common_txt369 || 'The account must be 8-13 digits'
        return
      }
      this.errorMsg = ''
      this.loading = true
      try {
        const areaCode = this.selectedAreaCode || this.GET_AREACODE
        if (!areaCode) {
          this.countryReadError = this.countryReadError || (this.$lang.phone_country_required || 'This feature is temporarily unavailable. Please try again later.')
          this.$toast({ message: this.countryReadError, icon: 'cross' })
          return
        }
        const params = {
          phoneNo: this.phoneNo,
          areaCode
        }
        const data = await BindPhone(params)
        if (handleNeedLogin(this, data)) return
        if (isSafeWriteSuccess(data) && isMineSafeWriteSuccess(data)) {
          this.bindBlockedReason = ''
          this.$toast({ message: this.$lang.Sucesso || 'Success', icon: 'success' })
          if (this.isFromGoogle) {
            const prefix = this.$route.params.prefix || safeGetStorageItem(getLocalStorage(), 'country') || ''
            this.$router.replace('/' + prefix + '/home')
            setTimeout(() => { reloadLocationSafely() }, 200)
          } else {
            this.$router.go(-1)
          }
        } else {
          this.bindBlockedReason = failMessage(data, 'Binding failed')
          this.errorMsg = this.bindBlockedReason
          this.$toast({ message: this.bindBlockedReason, icon: 'cross' })
        }
      } catch (e) {
        this.bindBlockedReason = phoneFailureMessage(e, 'This feature is temporarily unavailable. Please try again later.')
        this.errorMsg = this.bindBlockedReason
        this.$toast({ message: this.bindBlockedReason, icon: 'cross' })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;        /* 绿→火焰橙(强调/激活) */
@bg: @vk-bg;             /* 页面底 */
@input-bg: @vk-surface-2;/* 输入框面 */
@border: rgba(255, 255, 255, 0.08);

.phone-page {
  min-height: 100vh;
  background: @bg;
}

.phone-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  position: sticky;
  top: 0;
  z-index: 50;
  background: @bg;

  &__back {
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    cursor: pointer;
  }

  &__title {
    font-size: var(--vk-fs-md);
    font-weight: 700;
    color: #ffffff;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  &__spacer { width: 40px; }
}

.phone-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 24px;
  max-width: var(--vk-pc-col);
  margin: 0 auto;
}

.phone-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 90, 31, 0.1);
  border: 2px solid rgba(255, 90, 31, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.phone-desc {
  text-align: center;
  font-size: var(--vk-fs-base);
  color: var(--vk-t2);
  line-height: 1.6;
  max-width: 320px;
  margin-bottom: 32px;
}

.phone-field-row {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-bottom: 8px;
}

.phone-area-code {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 10px;
  height: 48px;
  background: @input-bg;
  border: 1.5px solid @border;
  border-radius: 10px;
  cursor: pointer;
  flex-shrink: 0;

  &__text {
    color: #ffffff;
    font-size: var(--vk-fs-sm);
    font-weight: 600;
    white-space: nowrap;
  }
}

.phone-input {
  height: 48px;
  background: @input-bg !important;
  border: 1.5px solid @border !important;
  border-radius: 10px !important;
  color: #ffffff;
  font-size: var(--vk-fs-base);

  /deep/ .van-field__control {
    color: #ffffff;
  }
  /deep/ .van-field__control::placeholder {
    color: var(--vk-t3);
  }
}

.phone-dropdown {
  background: #2a2f3e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  max-height: 200px;
  overflow-y: auto;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  margin-bottom: 8px;
  width: 100%;

  &__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    cursor: pointer;
    font-size: var(--vk-fs-sm);
    color: #ffffff;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);

    &:last-child { border-bottom: none; }
    &:active { background: rgba(255, 90, 31, 0.1); }
  }

  &__code {
    margin-left: auto;
    color: @primary;
    font-weight: 600;
    font-size: var(--vk-fs-xs);
  }
}

.phone-error {
  color: var(--vk-danger);
  font-size: var(--vk-fs-xs);
  padding: 4px 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.phone-submit {
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 24px;
  background: linear-gradient(135deg, @primary 0%, #17b366 100%);
  color: @bg;
  font-size: var(--vk-fs-md);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  margin-top: 16px;
  box-shadow: 0 4px 16px rgba(255, 90, 31, 0.3);

  &:active { transform: scale(0.97); }

  &--disabled {
    background: #3a3f4e;
    color: var(--vk-t3);
    box-shadow: none;
    cursor: not-allowed;
  }
}
</style>
